import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useAsync } from 'react-use';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useNotification } from '../../hooks/useNotification';
import { WorkspacesService } from '../../services/WorkspacesService';
import { Addon, CatalogData, Environment } from './types';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

const SYSTEM_ADD_SUCCESS_MESSAGE = 'System added successfully';
const VALIDATION_MESSAGES = {
  SYSTEM_REQUIRED: 'Please select a system',
  SYSTEM_NOT_FOUND: 'Selected system not found',
  ENV_WORKSPACE_REQUIRED: 'Environment and workspace ID are required',
} as const;

const useStyles = makeStyles(theme => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
    minWidth: 500,
  },
  formControl: {
    minWidth: '100%',
    marginBottom: theme.spacing(2),
  },
  addonsSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  emptyStateContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2.5),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: theme.shape.borderRadius,
  },
  warningIcon: {
    color: '#856404',
    marginRight: theme.spacing(1.5),
    marginTop: 2,
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5),
    backgroundColor: '#fdecea',
    border: '1px solid #f44336',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  errorIcon: {
    color: '#b71c1c',
    marginRight: theme.spacing(1),
    marginTop: 2,
    fontSize: 20,
  },
}));

interface WorkspaceSystemAddProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: { systemId: string; title: string }) => void;
  environment?: Environment;
  workspaceId?: string;
  availableSystems: Entity[];
}

const extractAddonsFromAnnotations = (selectedEntity: Entity): Set<string> => {
  const addonsAnnotation = selectedEntity.metadata.annotations?.['open.totvs.io/addons'];
  if (!addonsAnnotation) return new Set();

  return new Set(
    addonsAnnotation
      .split(',')
      .map((addon: string) => addon.trim())
  );
};

export const WorkspaceSystemAdd: React.FC<WorkspaceSystemAddProps> = ({
                                                                        open,
                                                                        onClose,
                                                                        onAdd,
                                                                        environment,
                                                                        workspaceId,
                                                                        availableSystems,
                                                                      }) => {
  const classes = useStyles();
  const { showSuccess, showError } = useNotification();
  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  const [selectedSystem, setSelectedSystem] = useState('');
  const [selectedAddon, setSelectedAddon] = useState<Addon>();
  const [error, setError] = useState('');
  const [insightsLoadError, setInsightsLoadError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi],
  );

  const selectedEntity = useMemo(() => {
    if (!selectedSystem || !availableSystems) return null;

    const selected = availableSystems.find(
      (entity: Entity) =>
        entity.metadata.annotations?.['open.totvs.io/app-code'] === selectedSystem
    );

    return selected;
  }, [selectedSystem, availableSystems]);

  const isInsightsAddon = (addon: Entity): boolean => {
    return addon.metadata.annotations?.['open.totvs.io/app-code'] === 'add001';
  }

  const buildAddonObject = async (addon: Entity): Promise<Addon | undefined> => {
    try {
      const components = isInsightsAddon(addon)
        ? await workspacesService.getInsightComponents(environment!, selectedEntity)
        : [];

      return {
        id: addon.metadata.annotations?.['open.totvs.io/app-code'] as string,
        ref: stringifyEntityRef(addon),
        name: addon.metadata.title || addon.metadata.name as string,
        insightsProductFolderName: addon.metadata.annotations?.['open.totvs.io/insights-folder-name'],
        components: components,
      };
    } catch(err) {
      if (isInsightsAddon(addon)) {
        const message = err instanceof Error ? err.message : 'Failed to load Insights addon resources.';
        setInsightsLoadError(message);
        return undefined;
      }
      throw err;
    }
  }

  const { value: allAddons, loading: allAddonsLoading } = useAsync(async (): Promise<Record<string, Addon>> => {
    const addonSystems = await catalogApi.getEntities({
      filter: {
        kind: 'System',
        'metadata.annotations.open.totvs.io/addon-system': 'true',
      },
      fields: [
        'kind',
        'metadata.title', 
        'metadata.name',
        'metadata.namespace',
        'metadata.annotations.open.totvs.io/app-code',
        'metadata.annotations.open.totvs.io/insights-databases',
        'metadata.annotations.open.totvs.io/datasharing-databases',
        'metadata.annotations.open.totvs.io/insights-folder-name'
      ],
    });

    const addonList = (await Promise.all(
      addonSystems.items.map((addon: Entity) => buildAddonObject(addon))
    )).filter((addon): addon is Addon => addon !== undefined);

    const addons = Object.fromEntries(
      addonList.map(addon => [addon.id, addon] as const)
    ) as Record<string, Addon>;

    return addons;
  }, [selectedEntity]);

  const availableAddons = useMemo((): Record<string, Addon> => {
    if (!selectedEntity || !allAddons) return {};

    const allowedAddonIds = extractAddonsFromAnnotations(selectedEntity);

    const availableAddonEntries = Object.entries(allAddons)
      .filter(([addonId]) => allowedAddonIds.has(addonId));

    const addons = Object.fromEntries(availableAddonEntries) as Record<string, Addon>;
    return addons;
  }, [selectedEntity, allAddons]);

  const addonsLoading = !!selectedSystem && allAddonsLoading;

  const handleSystemChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedSystem(event.target.value as string);
    setSelectedAddon(undefined);
    setError('');
    setInsightsLoadError(undefined);
  }, []);

  const handleAddonToggle = useCallback((addonId: string) => {
    setSelectedAddon(prev => prev?.id === addonId ? undefined : availableAddons[addonId]);
  }, [availableAddons]);

  const handleCancel = useCallback(() => {
    setSelectedSystem('');
    setSelectedAddon(undefined);
    setError('');
    setIsLoading(false);
    onClose();
  }, [onClose]);

  const handleAdd = useCallback(async () => {
    if (!selectedSystem) {
      setError(VALIDATION_MESSAGES.SYSTEM_REQUIRED);
      return;
    }

    if (!environment || !workspaceId) {
      showError(VALIDATION_MESSAGES.ENV_WORKSPACE_REQUIRED);
      return;
    }

    if (!selectedEntity) {
      setError(VALIDATION_MESSAGES.SYSTEM_NOT_FOUND);
      return;
    }

    setIsLoading(true);

    try {
      const addCommand: CatalogData = {
        system: {
          id: selectedEntity.metadata.annotations?.['open.totvs.io/app-code'] || '',
          ref: stringifyEntityRef(selectedEntity),
          name: selectedEntity.metadata.title || selectedEntity.metadata.name,
        },
        addon: selectedAddon,
      };

      await workspacesService.addWorkspaceSystem(environment, workspaceId, addCommand);

      onAdd({
        systemId: selectedSystem,
        title: selectedEntity.metadata.title || selectedEntity.metadata.name,
      });

      handleCancel();
      showSuccess(SYSTEM_ADD_SUCCESS_MESSAGE);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showError(`Failed to add system: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedSystem,
    selectedEntity,
    selectedAddon,
    environment,
    workspaceId,
    workspacesService,
    onAdd,
    showSuccess,
    showError,
    handleCancel,
  ]);

  const renderEmptyState = useCallback(() => (
    <Box className={classes.emptyStateContainer}>
      <Box className={classes.warningBox}>
        <WarningIcon className={classes.warningIcon} />
        <Box flex={1} textAlign="left">
          <Typography variant="subtitle1" style={{ color: '#856404', fontWeight: 600 }}>
            No systems available
          </Typography>
          <Typography variant="body2" style={{ color: '#856404', marginTop: 4 }}>
            There are no systems configured for provisioning in the catalog.
          </Typography>
        </Box>
      </Box>
    </Box>
  ), [classes]);

  const isAddButtonDisabled = useMemo(() => {
    return isLoading || !selectedSystem || availableSystems?.length === 0;
  }, [isLoading, selectedSystem, availableSystems]);

  const hasCatalogSystems = !!availableSystems?.length;
  const hasAvailableAddons = Object.keys(availableAddons).length > 0;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Typography variant="h6">Add System</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {!hasCatalogSystems && (
          renderEmptyState()
        )}

        {hasCatalogSystems && (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Select a system and its addons to add to the workspace.
              </Typography>
            </Box>

            <FormControl
              className={classes.formControl}
              variant="outlined"
              size="small"
              error={!!error}
            >
              <InputLabel id="system-select-label">Select System *</InputLabel>
              <Select
                labelId="system-select-label"
                value={selectedSystem}
                onChange={handleSystemChange}
                label="Select System *"
              >
                <MenuItem value="">
                  <em>Choose a system</em>
                </MenuItem>
                {availableSystems.map((entity: any) => {
                  const systemId = entity.metadata.annotations?.['open.totvs.io/app-code'];
                  const title = entity.metadata.title || entity.metadata.name;
                  return (
                    <MenuItem key={systemId} value={systemId}>
                      {title} ({systemId})
                    </MenuItem>
                  );
                })}
              </Select>
              {error && <FormHelperText error>{error}</FormHelperText>}
            </FormControl>

            {selectedSystem && (
              <Box className={classes.addonsSection}>
                <Typography variant="subtitle2" gutterBottom>
                  Available Addons
                </Typography>

                {addonsLoading && (
                  <Box display="flex" alignItems="center" py={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" style={{ marginLeft: 8 }}>
                      Loading addons...
                    </Typography>
                  </Box>
                )}

                {!addonsLoading && hasAvailableAddons && (
                  <FormGroup>
                    {Object.values(availableAddons).map((addon: Addon) => (
                      <FormControlLabel
                        key={addon.id}
                        control={
                          <Checkbox
                            checked={selectedAddon?.id === addon.id}
                            onChange={() => handleAddonToggle(addon.id)}
                            color="primary"
                          />
                        }
                        label={addon.name || addon.id}
                      />
                    ))}
                  </FormGroup>
                )}

                {!addonsLoading && !hasAvailableAddons && !insightsLoadError && (
                  <Typography variant="body2" color="textSecondary">
                    No addons available for this system
                  </Typography>
                )}

                {!addonsLoading && insightsLoadError && (
                  <Box className={classes.errorBox}>
                    <ErrorOutlineIcon className={classes.errorIcon} />
                    <Box flex={1}>
                      <Typography variant="subtitle2" style={{ color: '#d23a3a', fontWeight: 600 }}>
                        {insightsLoadError}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#d23a3a', marginTop: 2 }}>
                        This system supports the Insights addon, but we were unable to load its resources. You can still add the system without the addon and enable it later.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: 'flex-end', padding: '16px 24px' }}>
        <Button
          onClick={handleCancel}
          color="default"
          variant="outlined"
          style={{ marginRight: 8 }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          color="primary"
          variant="contained"
          disabled={isAddButtonDisabled}
        >
          {isLoading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
